import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { TrendingDown, Clock, Users, Award, Target, Zap } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const CountUpValue = ({
  target,
  prefix = "",
  suffix = "",
  duration = 1400,
  trigger = 0,
}) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let frameId;
    const startTime = performance.now();
    setValue(0);

    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);

      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [duration, target, trigger]);

  return <>{`${prefix}${Math.round(value)}${suffix}`}</>;
};

const statCardVariants = {
  initial: { opacity: 0, y: 30 },
  animate: ({ index, total, direction }) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: "easeOut",
      delay: (direction === "topToBottom" ? index : total - 1 - index) * 0.1,
    },
  }),
};

const Stats = () => {
  const sectionRef = useRef(null);
  const [countTrigger, setCountTrigger] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [staggerDirection, setStaggerDirection] = useState("topToBottom");

  const data = [
    {
      target: 95,
      suffix: "%",
      label: "Bias Reduction",
      icon: TrendingDown,
      gradient: "from-emerald-500 to-teal-400",
      description: "Fairer hiring decisions",
    },
    {
      target: 70,
      suffix: "%",
      label: "Time Saved",
      icon: Clock,
      gradient: "from-blue-500 to-cyan-400",
      description: "Faster recruitment process",
    },
    {
      target: 10,
      suffix: "k+",
      label: "Interviews Conducted",
      icon: Users,
      gradient: "from-violet-500 to-purple-400",
      description: "Successful placements",
    },
    {
      target: 98,
      suffix: "%",
      label: "Client Satisfaction",
      icon: Award,
      gradient: "from-amber-500 to-orange-400",
      description: "Happy customers",
    },
    {
      target: 50,
      suffix: "+",
      label: "Enterprise Clients",
      icon: Target,
      gradient: "from-pink-500 to-rose-400",
      description: "Trusted partnerships",
    },
    {
      target: 24,
      suffix: "/7",
      label: "AI Availability",
      icon: Zap,
      gradient: "from-indigo-500 to-blue-400",
      description: "Always ready to assist",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          setCountTrigger((prev) => prev + 1);
          setStaggerDirection((prev) =>
            prev === "topToBottom" ? "bottomToTop" : "topToBottom"
          );
        } else {
          setIsInView(false);
        }
      },
      { threshold: 0.25 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative px-6 md:px-16 py-20 bg-gradient-to-br from-[#0a1f44] via-[#0a2a5e] to-[#1a3a6e] overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div className="text-center mb-12" {...fadeInUp}>
          <span className="inline-block px-4 py-1.5 bg-[#0a1f44]/50 backdrop-blur-sm border border-blue-400/20 rounded-full text-cyan-300 text-sm font-medium mb-4">
            Our Impact
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Numbers That{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Speak
            </span>
          </h2>
          <p className="text-lg text-blue-200/70 max-w-2xl mx-auto">
            Real results from our AI-powered recruitment platform
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5"
          initial="initial"
          animate={isInView ? "animate" : "initial"}
        >
          {data.map((item, i) => {
            const IconComponent = item.icon;
            return (
              <motion.div
                key={i}
                variants={statCardVariants}
                custom={{
                  index: i,
                  total: data.length,
                  direction: staggerDirection,
                }}
                className="group bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-2xl shadow-lg hover:shadow-xl hover:bg-white/10 transition-all duration-500 text-center relative overflow-hidden"
              >
                {/* Icon */}
                <div
                  className="w-12 h-12 bg-[#0a1f44] border border-blue-400/20 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:bg-[#0a2a5e]/50 group-hover:border-blue-400/40 transition-all duration-300"
                >
                  <IconComponent className="w-6 h-6 text-cyan-400" strokeWidth={2} />
                </div>

                {/* Value */}
                <h3 className="text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent mb-1">
                  <CountUpValue
                    target={item.target}
                    suffix={item.suffix}
                    trigger={countTrigger}
                    duration={1500 + i * 120}
                  />
                </h3>

                {/* Label */}
                <p className="text-blue-100 font-medium text-sm mb-1">
                  {item.label}
                </p>

                {/* Description */}
                <p className="text-blue-200/65 text-xs">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Stats;
